import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Grid,
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Stepper,
  Step,
  StepLabel,
  Container,
  Paper,
  useTheme,
  alpha,
  Chip,
  FormControlLabel,
  Switch,
  Stack,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from "@mui/material";
import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useHandleWorkFlow } from "0.Mine/1.Hooks/CRM/workflow";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";
import { WorkFlow } from "0.Mine/3.Interfaces/workFlow";
import { SafeArgonButton } from "0.Mine/4.Argon/ArgonComponents";

import { mockMessages, templates, templateCategories } from "./TemplateData";
import TemplateStepEditor, { StepData } from "./TemplateStepViewer";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
    },
  },
};

// Define prop types for the custom buttons
interface BackButtonProps {
  onClick: () => void;
  disabled?: boolean; // Make disabled optional
}

interface ForwardButtonProps {
  onClick: () => void;
  disabled?: boolean;
  text: string;
  endIcon?: boolean;
}

interface SelectButtonProps {
  onClick: () => void;
  text: string;
}

const TemplateSequenceSelector = () => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [editingStep, setEditingStep] = useState<StepData | null>(null);
  const [isStepEditorOpen, setIsStepEditorOpen] = useState(false);
  const [mockSteps, setMockSteps] = useState<StepData[]>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [stepToDelete, setStepToDelete] = useState<number | null>(null);
  const { submitNewWorkflow } = useHandleWorkFlow();
  const steps = [
    "Seleziona la categoria del template",
    "Scegli il template",
    "Configura il workflow",
    "Rivedi e salva",
  ];
  const { session } = useBusinessContext();
  const filteredTemplates = selectedCategory
    ? templates.filter((template) => template.category === selectedCategory)
    : templates;

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setActiveStep(1);
  };
  const navigate = useNavigate();

  const handleTemplateSelect = (templateId: number) => {
    setSelectedTemplate(templateId);
    setActiveStep(2);
  };

  const handleBack = () => {
    if (activeStep === 0) {
      console.log("Active step equals 0");
      navigate(-1);
      return;
    }

    setActiveStep((prevStep) => prevStep - 1);

    if (activeStep === 1) {
      setSelectedCategory(null);
    }
    if (activeStep === 2) {
      setSelectedTemplate(null);
    }
  };

  const selected = templates.find((t) => t.id === selectedTemplate);
  const { t } = useTranslation();
  // Initialize mockSteps when selected template changes
  useEffect(() => {
    if (selected) {
      // Get the appropriate mock messages for this template category
      const categoryMessages = mockMessages[selected.category] || [];

      // Create steps based on the template's step count
      const stepCount = Math.min(selected.steps || 0, categoryMessages.length);

      setMockSteps(
        Array.from({ length: stepCount }, (_, index) => {
          const messageTemplate = categoryMessages[index % categoryMessages.length];

          return {
            id: index + 1,
            title:
              selected.category === "email"
                ? `Email ${index + 1}: ${messageTemplate.subject || ""}`
                : `${
                    selected.category.charAt(0).toUpperCase() + selected.category.slice(1)
                  } Message ${index + 1}`,
            description:
              selected.category === "email"
                ? `Inviato ${index + 1} giorn${index > 0 ? "i" : "o"} dopo la registrazione`
                : `Inviato ${index + 1} giorn${
                    index > 0 ? "i" : "o"
                  } dopo il passaggio preceedente`,
            type: messageTemplate.type || selected.category,
            content: messageTemplate.content || "",
            subject: messageTemplate.subject || "",
            delay: index + 1,
            delayUnit: "giorni",
          };
        })
      );
    }
  }, [selected]);

  if (!session) return <p>Please select a business to continue</p>;

  const handleEditStep = (stepId: number) => {
    const stepToEdit = mockSteps.find((step) => step.id === stepId) || null;
    setEditingStep(stepToEdit);
    setIsStepEditorOpen(true);
  };

  const handleAddStep = () => {
    setEditingStep(null);
    setIsStepEditorOpen(true);
  };

  const handleSaveStep = (step: StepData) => {
    if (editingStep) {
      // Update existing step
      setMockSteps(mockSteps.map((s) => (s.id === step.id ? step : s)));
    } else {
      // Add new step with unique ID
      const newStep = {
        ...step,
        id: Date.now(), // Ensure unique ID
      };
      setMockSteps([...mockSteps, newStep]);
    }

    // Close the editor
    setIsStepEditorOpen(false);
  };

  const getPopularityColor = (popularity: string) => {
    switch (popularity) {
      case "popular":
        return theme.palette.success.main;
      case "new":
        return theme.palette.info.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  // Create a standard Button component that doesn't rely on SafeArgonButton
  const BackButton: React.FC<BackButtonProps> = ({ onClick, disabled = false }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "8px 16px",
        backgroundColor: "transparent",
        color: theme.palette.primary.main,
        border: `1px solid ${theme.palette.primary.main}`,
        borderRadius: "4px",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1,
        fontWeight: 500,
        fontSize: "0.875rem",
        transition: "all 0.2s",
        outline: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {t("kanban.cancel")}
    </button>
  );

  // Create a standard Forward Button component
  const ForwardButton: React.FC<ForwardButtonProps> = ({
    onClick,
    disabled = false,
    text,
    endIcon = false,
  }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "8px 16px",
        backgroundColor: theme.palette.primary.main,
        color: "white",
        border: "none",
        borderRadius: "4px",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1,
        fontWeight: 500,
        fontSize: "0.875rem",
        transition: "all 0.2s",
        outline: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
      }}
    >
      {text}
      {endIcon && <ArrowForwardIcon fontSize="small" />}
    </button>
  );

  // Use standard Material-UI Button as a backup
  const SelectButton: React.FC<SelectButtonProps> = ({ onClick, text }) => (
    <button
      onClick={onClick}
      style={{
        padding: "6px 12px",
        backgroundColor: theme.palette.primary.main,
        color: "white",
        border: `1px solid ${theme.palette.primary.main}`,
        borderRadius: "4px",
        cursor: "pointer",
        fontWeight: 600,
        fontFamily: "Poppins, sans-serif",
        fontSize: "0.75rem",
        transition: "all 0.2s",
        outline: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "4px",
        margin: "auto",
      }}
    >
      {text}
      <ArrowForwardIcon fontSize="small" />
    </button>
  );

  const handleDeleteStep = (stepId: number) => {
    // Set the step ID to delete and open the confirmation dialog
    setStepToDelete(stepId);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteStep = () => {
    if (stepToDelete !== null) {
      // Filter out the step to delete from mockSteps
      setMockSteps(mockSteps.filter((step) => step.id !== stepToDelete));
    }
    // Close the confirmation dialog and reset stepToDelete
    setDeleteConfirmOpen(false);
    setStepToDelete(null);
  };

  const handleSubmit = async () => {
    // Prepare data for backend submission
    const workflowData = {
      templateId: selectedTemplate,
      templateType: selected?.category,
      templateName: selected?.name,
      sendReports: true,
      steps: mockSteps.map((step, index) => ({
        id: step.id,
        title: step.title,
        description: step.description,
        type: step.type,
        content: step.content,
        subject: step.subject,
        delay: step.delay,
        delayUnit: step.delayUnit,
        condition: step.condition,
        position: index + 1,
      })),
      created: new Date().toISOString(),
    } as WorkFlow;

    // Log data to console
    console.log("Workflow submission data:", workflowData);
    const response = await submitNewWorkflow(session.selectedBusiness.id, workflowData);
    navigate("/crm/workflow");
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <motion.div variants={containerVariants} initial="hidden" animate="visible">
            <Box
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 4 }}
            >
              <Box>
                <Typography variant="h4" gutterBottom>
                  {t("workflows.SelectWorkflowCategory")}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {t("workflows.ChooseType")}
                </Typography>
              </Box>
              <BackButton onClick={handleBack} disabled={false} />
            </Box>

            <Grid container spacing={3}>
              {templateCategories.map((category) => (
                <Grid item xs={12} sm={6} md={4} key={category.id}>
                  <motion.div variants={itemVariants}>
                    <Card
                      sx={{
                        height: "100%",
                        paddingTop: "20px",
                        display: "flex",
                        flexDirection: "column",
                        transition: "all 0.3s",
                        cursor: "pointer",
                        "&:hover": {
                          transform: "translateY(-8px)",
                          boxShadow: 6,
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                        },
                      }}
                      onClick={() => handleCategorySelect(category.id)}
                    >
                      <Box
                        sx={{
                          p: 3,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          color: theme.palette.primary.main,
                          background: "#e2e8ff",
                          padding: "10px",
                          borderRadius: "99px",
                          margin: "auto",
                          width: "max-content",
                        }}
                      >
                        {category.icon}
                      </Box>
                      <CardContent sx={{ textAlign: "center", flexGrow: 1 }}>
                        <Typography variant="h5" component="h2">
                          {category.name}
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                          <SelectButton
                            onClick={() => handleCategorySelect(category.id)}
                            text={t("commonText.select")}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        );
      case 1:
        return (
          <motion.div variants={containerVariants} initial="hidden" animate="visible">
            <Box
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 4 }}
            >
              <Box>
                <Typography variant="h4" gutterBottom>
                  {t("workflows.tplChoose")}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {t("workflows.ChooseTplType")}
                </Typography>
              </Box>
              <BackButton onClick={handleBack} disabled={false} />
            </Box>

            <Grid container spacing={3}>
              {filteredTemplates.map((template) => (
                <Grid item xs={12} sm={6} md={4} key={template.id}>
                  <motion.div variants={itemVariants}>
                    <Card
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        transition: "all 0.3s ease",
                        cursor: template.comingSoon ? "default" : "pointer",
                        border: "1px solid transparent",
                        "&:hover": {
                          transform: template.comingSoon ? "none" : "translateY(-4px)",
                          boxShadow: template.comingSoon
                            ? "none"
                            : "0 6px 20px rgba(0, 0, 0, 0.08)",
                          borderColor: template.comingSoon
                            ? "transparent"
                            : theme.palette.grey[300],
                          backgroundColor: template.comingSoon
                            ? "transparent"
                            : theme.palette.grey[50],
                        },
                        ...(selectedTemplate === template.id && {
                          border: `2px solid ${theme.palette.primary.main}`,
                          boxShadow: 6,
                        }),
                        // Add overlay effect for coming soon templates
                        filter: template.comingSoon ? "grayscale(0.5)" : "none",
                        opacity: template.comingSoon ? 0.85 : 1,
                      }}
                      onClick={() => !template.comingSoon && handleTemplateSelect(template.id)}
                    >
                      <CardMedia
                        component="img"
                        height="190"
                        image={template.image}
                        alt={template.name}
                        sx={{ margin: "0!important", borderRadius: "10px 10px 0 0" }}
                      />
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                          }}
                        >
                          <Typography
                            variant="h6"
                            component="h2"
                            sx={{ color: "#192143", paddingTop: "15px", paddingBottom: "3px" }}
                          >
                            {template.name}
                          </Typography>
                          <Chip
                            label={
                              template.comingSoon
                                ? t("commonText.comingSoon")
                                : t("commonText.popular")
                            }
                            size="small"
                            sx={{
                              color: "#ffffff",
                              marginTop: "-50px",
                              background: template.comingSoon ? "#f5365c" : "#2dce89",
                              fontWeight: "600",
                            }}
                          />
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2, fontSize: "14px", lineHeight: "20px" }}
                        >
                          {template.description}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#37386e", fontSize: "14px", fontWeight: "600" }}
                        >
                          {template.steps} {t("commonText.steps")}
                        </Typography>
                      </CardContent>
                      <Box sx={{ pt: 0 }}>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent card click event
                            if (!template.comingSoon) {
                              handleTemplateSelect(template.id);
                            }
                          }}
                          sx={{
                            width: "100%",
                            py: 1,
                            px: 2,
                            bgcolor: template.comingSoon
                              ? theme.palette.grey[400]
                              : theme.palette.primary.main,
                            color: "#ffffff!important",
                            borderRadius: "0px!important",
                            fontWeight: 500,
                            fontSize: "0.875rem",
                            transition: "all 0.2s",
                            "&:hover": {
                              bgcolor: template.comingSoon
                                ? theme.palette.grey[500]
                                : theme.palette.primary.dark,
                            },
                            pointerEvents: template.comingSoon ? "none" : "auto", // Disable button clicks for coming soon
                          }}
                        >
                          {template.comingSoon ? t("commonText.comingSoon") : t("workflows.useTpl")}
                        </Button>
                      </Box>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            style={{ width: "100%" }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 4 }}
            >
              <Box>
                <Typography variant="h4" gutterBottom>
                  {t("workflows.configureWorkflow")}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {selected?.name} - {t("workflows.review")}
                </Typography>
              </Box>
              <BackButton onClick={handleBack} disabled={false} />
            </Box>

            {/* Step list */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">Workflow Steps</Typography>
                <button
                  onClick={handleAddStep}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "6px 12px",
                    backgroundColor: theme.palette.primary.main,
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontWeight: 500,
                    fontSize: "0.75rem",
                  }}
                >
                  <AddIcon fontSize="small" /> {t("workflows.addStep")}
                </button>
              </Box>

              <Stack spacing={2}>
                {mockSteps.map((step, index) => (
                  <Paper
                    key={step.id}
                    elevation={1}
                    sx={{
                      p: 2,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      bgcolor: "#f6fbff",
                      borderRadius: "20px",
                      boxShadow: "unset!important",
                      border: "1px solid #ebebeb",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          bgcolor: theme.palette.primary.main,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#ffffff",
                          mr: 2,
                          fontWeight: "600",
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ color: "#37386e", fontSize: "1.2rem", fontWeight: "600" }}
                        >
                          {step.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ color: "#525252", fontSize: "14px", fontWeight: "400" }}
                        >
                          {step.description}
                        </Typography>
                        {step.type === "email" && step.subject && (
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            <strong style={{ color: "37386e" }}>{t("commonText.subject")}:</strong>{" "}
                            {step.subject}
                          </Typography>
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 0.5,
                            display: "-webkit-box",
                            overflow: "hidden",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: 2,
                            fontSize: "14px",
                            color: "#5a6eb4",
                          }}
                        >
                          {step.content.substring(0, 120)}
                          {step.content.length > 120 ? "..." : ""}
                        </Typography>
                      </Box>
                    </Box>
                    <Box>
                      <IconButton color="primary" onClick={() => handleEditStep(step.id)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDeleteStep(step.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}
              </Stack>

              {mockSteps.length === 0 && (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    Non sono presenti degli step. Clicca Aggiungi un passaggio per iniziare.
                  </Typography>
                </Box>
              )}
            </Paper>

            <TemplateStepEditor
              open={isStepEditorOpen}
              onClose={() => setIsStepEditorOpen(false)}
              onSave={handleSaveStep}
              step={editingStep}
            />
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <ForwardButton
                onClick={() => setActiveStep(3)}
                disabled={mockSteps.length === 0}
                text={t("workflows.continueReview")}
                endIcon={true}
              />
            </Box>
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            style={{ width: "100%" }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 4 }}
            >
              <Box>
                <Typography variant="h4" gutterBottom>
                  Rivedi e Salva
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Controlla i dettagli del tuo workflow prima di attivarlo.
                </Typography>
              </Box>
              <BackButton onClick={handleBack} disabled={false} />
            </Box>

            {/* Workflow summary */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Resoconto del workflow
              </Typography>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Tipo di template
                    </Typography>
                    <Typography variant="body1">
                      {selected?.category.charAt(0).toUpperCase() + selected?.category.slice(1)}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Nome Template
                    </Typography>
                    <Typography variant="body1">{selected?.name}</Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Numero di Passaggi
                    </Typography>
                    <Typography variant="body1">{mockSteps.length}</Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Durata totale
                    </Typography>
                    <Typography variant="body1">
                      {mockSteps.reduce((sum, step) => sum + (step.delay || 0), 0)} giorni
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Resoconto passaggi
                </Typography>
              </Box>

              {mockSteps.map((step, index) => (
                <Paper
                  key={step.id}
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    borderRadius: 1,
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={8}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            bgcolor: theme.palette.primary.main,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            mr: 2,
                            fontSize: "0.875rem",
                          }}
                        >
                          {index + 1}
                        </Box>
                        <Box>
                          <Typography variant="subtitle1">{step.title}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {step.type === "email"
                              ? "Email"
                              : step.type === "whatsapp"
                              ? "WhatsApp"
                              : step.type === "sms"
                              ? "SMS"
                              : "Call Script"}
                          </Typography>
                          {step.content && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mt: 0.5,
                                display: "-webkit-box",
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {step.content.substring(0, 60)}
                              {step.content.length > 60 ? "..." : ""}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={4}
                      sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}
                    >
                      <Typography variant="body2">
                        {index === 0
                          ? `${step.delay || 0} ${step.delayUnit || "days"} dopo la registrazione`
                          : `${step.delay || 0} ${step.delayUnit || "days"} dopo step ${index}`}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Paper>

            {/* Workflow settings */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Settaggi Workflow
              </Typography>

              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Switch checked={true} color="primary" />}
                    label="Attiva workflow immediatamente"
                  />
                </Grid>
                {/*<Grid item xs={12} md={6}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={true}
                                                color="primary"
                                            />
                                        }
                                        label="Ricevi report delle performance"
                                    />
                                </Grid>*/}
              </Grid>
            </Paper>

            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <button
                style={{
                  padding: "10px 24px",
                  backgroundColor: theme.palette.primary.main,
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: "1rem",
                }}
                onClick={handleSubmit}
              >
                Salva Workflow
              </button>
            </Box>
          </motion.div>
        );
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 5 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </motion.div>

        <Grid container spacing={2} sx={{ padding: 2 }}>
          <Grid item xs={12}>
            {renderStepContent()}
          </Grid>
        </Grid>

        {/* Dialog moved here so it's available for all steps */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{t("workflows.confirmDelete")}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {t("workflows.deleteStepWarning")}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)} color="primary">
              {t("commonText.cancel")}
            </Button>
            <Button onClick={confirmDeleteStep} color="error" autoFocus>
              {t("commonText.delete")}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default TemplateSequenceSelector;
